import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

export interface CiudadCategoria {
  ciudad_slug: string;
  ciudad: string;
  total_empresas: number;
}

interface UseCategoryCitiesReturn {
  ciudades: CiudadCategoria[];
  loading: boolean;
  error: string | null;
  categoriaLabel: string;
  total: number;
}

/* Convierte slug a texto legible: "mi-ciudad" → "Mi Ciudad" */
const humanizeSlug = (slug: string) =>
  slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const useCategoryCities = (
  categoriaSlug: string
): UseCategoryCitiesReturn => {
  const [ciudades, setCiudades] = useState<CiudadCategoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoriaLabel, setCategoriaLabel] = useState(() =>
    humanizeSlug(categoriaSlug)
  );

  useEffect(() => {
    if (!categoriaSlug) return;

    const fetchCiudades = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: rpcError } = await supabase.rpc(
          'get_empresas_cliente',
          { p_email: null }
        );

        if (rpcError) throw rpcError;

        const todas =
          (data as Array<{
            categoria_slug: string;
            categoria?: string | null;
            ciudad_slug: string;
            ciudad?: string | null;
          }>) ?? [];

        const filtradas = todas.filter(
          (e) =>
            e.categoria_slug === categoriaSlug &&
            e.ciudad_slug &&
            e.ciudad_slug.trim() !== ''
        );

        const mapa = new Map<string, CiudadCategoria>();

        for (const e of filtradas) {
          const key = e.ciudad_slug;

          if (!mapa.has(key)) {
            mapa.set(key, {
              ciudad_slug: e.ciudad_slug,
              ciudad: e.ciudad ?? humanizeSlug(e.ciudad_slug),
              total_empresas: 1,
            });
          } else {
            const existente = mapa.get(key)!;
            existente.total_empresas += 1;
          }
        }

        const ciudadesAgrupadas = Array.from(mapa.values()).sort((a, b) => {
          if (b.total_empresas !== a.total_empresas) {
            return b.total_empresas - a.total_empresas;
          }
          return a.ciudad.localeCompare(b.ciudad, 'es');
        });

        setCiudades(ciudadesAgrupadas);

        setCategoriaLabel(
          filtradas[0]?.categoria ?? humanizeSlug(categoriaSlug)
        );
      } catch (err: any) {
        Sentry.captureException(err, {
          extra: { categoriaSlug },
        });
        setError(err.message || 'Error al cargar ciudades');
      } finally {
        setLoading(false);
      }
    };

    fetchCiudades();
  }, [categoriaSlug]);

  return {
    ciudades,
    loading,
    error,
    categoriaLabel,
    total: ciudades.length,
  };
};
